import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { SbSharedRefineMenuMultiSelectList } from '../SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelectList.jsx';
import { sbSharedRefineMenuMessages } from '../sbSharedRefineMenuMessages.tsx';
import { useMeasurementUnit } from '../../useMeasurementUnit.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type SbSharedRefineMenuFilterTableSize_itemSearch$key } from './__generated__/SbSharedRefineMenuFilterTableSize_itemSearch.graphql.ts';
import { type SbSharedRefineMenuFilterTableSize_filters$key } from './__generated__/SbSharedRefineMenuFilterTableSize_filters.graphql.ts';
import { type SbSharedRefineMenuFilterTableSize_user$key } from './__generated__/SbSharedRefineMenuFilterTableSize_user.graphql.ts';

import dibsCss from 'dibs-css';

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuFilterTableSize_itemSearch on ItemSearchQueryConnection {
        ...SbSharedRefineMenuMultiSelectList_itemSearch
        ...useMeasurementUnit_itemSearch
    }
`;

const filtersFragment = graphql`
    fragment SbSharedRefineMenuFilterTableSize_filters on SearchBrowseFilter {
        name
        values {
            urlLabel
            linkReference
            count
        }
    }
`;

const userFragment = graphql`
    fragment SbSharedRefineMenuFilterTableSize_user on User {
        ...useMeasurementUnit_user
    }
`;

type Props = {
    itemSearch: SbSharedRefineMenuFilterTableSize_itemSearch$key;
    filters: SbSharedRefineMenuFilterTableSize_filters$key | null;
    user: SbSharedRefineMenuFilterTableSize_user$key;
};

export const SbSharedRefineMenuFilterTableSize: FC<Props> = ({
    itemSearch: itemSearchRef,
    filters: filtersRef,
    user: userRef,
}) => {
    const intl = useIntl();
    const { isMobile } = useServerVarsContext();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filters = useFragment(filtersFragment, filtersRef);
    const user = useFragment(userFragment, userRef);
    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });
    const tableWidthRange = (filters?.values || []).filter(filterFalsy).map(filterValue => ({
        ...filterValue,
        displayName: sbSharedRefineMenuMessages.formatMeasurementFromUrlLabel({
            intl,
            urlLabel: filterValue?.urlLabel || null,
            measurementUnit,
            shouldConvertValue: true,
        }),
    }));

    if (!tableWidthRange.length) {
        return null;
    }

    return (
        <div className={isMobile ? dibsCss.mtSmall : undefined}>
            <SbSharedRefineMenuMultiSelectList
                itemSearch={itemSearch}
                filterName={filters?.name}
                shouldUseExpandableList={false}
                values={tableWidthRange}
            />
        </div>
    );
};
